import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  amountWithCurrencySymbol,
  CurrencyCode,
  DEFAULT_CURRENCIES,
  isCryptoCurrency,
  mapLocaleCurrencyCode,
} from '@/helpers/currency';
import { findNearestStake } from '@/helpers/jackpot';
import { useGetCommonStore } from '@/store';

const EXCHANGE_ROUNDING = 100;

const SUPPORTED_CURRENCIES = new Set([
  CurrencyCode.USD,
  CurrencyCode.EUR,
  CurrencyCode.CAD,
  CurrencyCode.AUD,
  CurrencyCode.NOK,
  CurrencyCode.NZD,
]);

const getLocaleCurrency = (language: string): CurrencyCode => {
  const mapped = mapLocaleCurrencyCode[language] as CurrencyCode;
  return SUPPORTED_CURRENCIES.has(mapped) ? mapped : CurrencyCode.USD;
};

export const useExchangeAmount = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const useCommonStore = useGetCommonStore();
  const user = useCommonStore.use.user();
  const exchangeRates = useCommonStore.use.exchangeRates();

  const displayCurrency = useMemo((): CurrencyCode => {
    const localeCurrency = getLocaleCurrency(language);

    if (!user) return localeCurrency;

    const userCurrency = user.currency as CurrencyCode;
    const isCrypto = isCryptoCurrency(DEFAULT_CURRENCIES, userCurrency);

    if (isCrypto || !SUPPORTED_CURRENCIES.has(userCurrency)) {
      return CurrencyCode.USD;
    }

    return userCurrency;
  }, [user, language]);

  const getExchangeAmount = useCallback(
    (amount: number, inputCurrency = CurrencyCode.USD): number => {
      if (!exchangeRates || displayCurrency === inputCurrency) {
        return amount;
      }

      const rate =
        exchangeRates.fiat?.[inputCurrency]?.[displayCurrency]?.exchangeRate ??
        1;

      return (
        Math.floor((amount / rate) * EXCHANGE_ROUNDING) / EXCHANGE_ROUNDING
      );
    },
    [exchangeRates, displayCurrency]
  );

  const formatAmount = useCallback(
    (amount: number, withCurrency = true, fractionDigits?: number): string => {
      const formatted = amount.toLocaleString(language, {
        minimumFractionDigits: fractionDigits ?? 0,
        maximumFractionDigits: fractionDigits ?? 2,
      });

      return withCurrency
        ? amountWithCurrencySymbol(displayCurrency, formatted, {
            noDecimals: true,
          })
        : formatted;
    },
    [language, displayCurrency]
  );

  const formatExchangedAmount = useCallback(
    (
      amount: number,
      inputCurrency = CurrencyCode.USD,
      fractionDigits?: number
    ): string =>
      formatAmount(
        getExchangeAmount(amount, inputCurrency),
        true,
        fractionDigits
      ),
    [getExchangeAmount, formatAmount]
  );

  const formatMinBet = useCallback(
    (minBetUsd: number): string => {
      const converted = getExchangeAmount(minBetUsd, CurrencyCode.USD);
      const nearestStake = findNearestStake(converted, displayCurrency);
      return formatAmount(nearestStake);
    },
    [getExchangeAmount, displayCurrency, formatAmount]
  );

  return {
    displayCurrency,
    getExchangeAmount,
    formatAmount,
    formatExchangedAmount,
    formatMinBet,
  };
};
